import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import { SellerSpinner } from '../components/skeleton';
import { AuthContext } from '../components/AuthContext';
import PaymentForm from '../components/PaymentForm';
import '../styles/SellerCommon.css';
import './SellerBillingPage.css';

function SellerBillingPage() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [message, setMessage] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [charges, setCharges] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [chargesPagination, setChargesPagination] = useState({});
  const [invoicesPagination, setInvoicesPagination] = useState({});
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  useEffect(() => {
    loadBillingInfo();
    loadPaymentMethods();
    loadCharges();
    loadInvoices();
  }, []);

  const loadBillingInfo = async () => {
    try {
      const response = await API.get('/seller/billing-info');
      setBillingInfo(response.data.billingInfo);
      
      // Controlla se i dati sono completi per decidere se mostrare il form
      const billingData = response.data.billingInfo;
      const requiredFields = ['business_name', 'phone_number', 'vat_number', 'fiscal_code', 'billing_address', 'billing_city', 'billing_zip', 'billing_province', 'billing_country'];
      const hasUnivocalOrPec = billingData?.univocal_code || billingData?.pec;
      
      const isComplete = billingData && 
        requiredFields.every(field => billingData[field]) && 
        hasUnivocalOrPec;
      
      setShowBillingForm(!isComplete);
    } catch (error) {
      console.error('Errore nel caricamento informazioni fatturazione:', error);
      setMessage('Errore nel caricamento delle informazioni');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
              const response = await API.get('/payment/payment-methods');
      if (response.data.success) {
        setPaymentMethods(response.data.data || []);
      } else {
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento metodi di pagamento:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const loadCharges = async (page = 1) => {
    try {
      const response = await API.get(`/payment/charges?page=${page}&limit=10`);
      if (response.data.success) {
        setCharges(response.data.charges || []);
        setChargesPagination(response.data.pagination || {});
      } else {
        setCharges([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento transazioni:', error);
      setCharges([]);
    }
  };

  const loadInvoices = async (page = 1) => {
    try {
      const response = await API.get(`/payment/invoices?page=${page}&limit=10`);
      if (response.data.success) {
        setInvoices(response.data.invoices || []);
        setInvoicesPagination(response.data.pagination || {});
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento fatture:', error);
      setInvoices([]);
    }
  };

  const handlePaymentMethodSuccess = () => {
    setMessage('Metodo di pagamento salvato con successo!');
    loadPaymentMethods();
    setShowPaymentModal(false);
  };

  const handlePaymentMethodError = (error) => {
    setMessage(`Errore nel salvataggio: ${error}`);
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
              const response = await API.post('/payment/payment-methods/default', {
        payment_method_id: paymentMethodId
      });
      
      if (response.data.success) {
        setMessage('Metodo di pagamento impostato come default');
        loadPaymentMethods();
      }
    } catch (error) {
      console.error('Errore nell\'impostazione metodo default:', error);
      setMessage('Errore nell\'impostazione metodo di pagamento default');
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo metodo di pagamento?')) {
      return;
    }

    try {
              const response = await API.delete('/payment/payment-methods', {
        data: { payment_method_id: paymentMethodId }
      });
      
      if (response.data.success) {
        setMessage('Metodo di pagamento rimosso con successo');
        loadPaymentMethods();
      }
    } catch (error) {
      console.error('Errore nella rimozione metodo di pagamento:', error);
      setMessage('Errore nella rimozione metodo di pagamento');
    }
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveBillingInfo = async (e) => {
    e.preventDefault();
    
    try {
      const response = await API.put('/seller/billing-info', billingInfo);
      
      if (response.data.success) {
        setMessage('Informazioni di fatturazione aggiornate con successo!');
        setShowBillingForm(false); // Chiudi il form dopo il salvataggio
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento:', error);
      setMessage('Errore nell\'aggiornamento delle informazioni');
    }
  };

  const handleDocumentUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    
    // Usa i nomi dei campi corretti per le nuove route
    if (documentType === 'visura') {
      formData.append('visuraDoc', file);
    } else if (documentType === 'identity') {
      formData.append('identityDoc', file);
    }

    try {
      setUploadingDoc(true);
      
      // Usa le route specifiche invece della generica
      const endpoint = documentType === 'visura' ? '/seller/upload-visura-doc' : '/seller/upload-identity-doc';
      const response = await API.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMessage('Documento caricato con successo!');
        loadBillingInfo(); // Ricarica per aggiornare i documenti
      }
    } catch (error) {
      console.error('Errore nel caricamento documento:', error);
      setMessage('Errore nel caricamento del documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentRemove = async (documentType) => {
    try {
      setUploadingDoc(true);
      
      // Usa le route specifiche per la rimozione
      const endpoint = documentType === 'visura' ? '/seller/remove-visura-doc' : '/seller/remove-identity-doc';
      const response = await API.post(endpoint);

      if (response.data.success) {
        setMessage('Documento rimosso con successo!');
        loadBillingInfo(); // Ricarica per aggiornare i documenti
      }
    } catch (error) {
      console.error('Errore nella rimozione documento:', error);
      setMessage('Errore nella rimozione del documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const BillingSummary = () => (
    <div className="billing-summary">
      <div className="summary-header">
        <h3>Informazioni di Fatturazione</h3>
        <button 
          className="btn-secondary"
          onClick={() => setShowBillingForm(true)}
        >
          Modifica
        </button>
      </div>
      
      <div className="summary-content">
        <div className="summary-row">
          <div className="summary-item">
            <label>Nome Azienda:</label>
            <span>{billingInfo?.business_name}</span>
          </div>
          <div className="summary-item">
            <label>Telefono:</label>
            <span>{billingInfo?.phone_number}</span>
          </div>
        </div>
        
        <div className="summary-row">
          <div className="summary-item">
            <label>Partita IVA:</label>
            <span>{billingInfo?.vat_number}</span>
          </div>
          <div className="summary-item">
            <label>Codice Fiscale:</label>
            <span>{billingInfo?.fiscal_code || 'Non specificato'}</span>
          </div>
          <div className="summary-item">
            <label>Codice Univoco/PEC:</label>
            <span>{billingInfo?.univocal_code || billingInfo?.pec || 'Non specificato'}</span>
          </div>
        </div>
        
        <div className="summary-row">
          <div className="summary-item full-width">
            <label>Indirizzo di Fatturazione:</label>
            <span>
              {billingInfo?.billing_address}, {billingInfo?.billing_zip} {billingInfo?.billing_city} ({billingInfo?.billing_province}), {billingInfo?.billing_country}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const Pagination = ({ pagination, onPageChange, type }) => {
    if (!pagination || pagination.totalPages <= 1) return null;
    
    return (
      <div className="pagination">
        <button 
          className="pagination-btn"
          onClick={() => onPageChange(pagination.page - 1)}
          disabled={pagination.page <= 1}
        >
          ← Precedente
        </button>
        
        <span className="pagination-info">
          Pagina {pagination.page} di {pagination.totalPages} 
          ({pagination.total} {type === 'charges' ? 'transazioni' : 'fatture'})
        </span>
        
        <button 
          className="pagination-btn"
          onClick={() => onPageChange(pagination.page + 1)}
          disabled={pagination.page >= pagination.totalPages}
        >
          Successiva →
        </button>
      </div>
    );
  };

  const PaymentMethodModal = () => (
    <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Configura Metodo di Pagamento</h2>
          <button 
            className="modal-close" 
            onClick={() => setShowPaymentModal(false)}
          >
            ×
          </button>
        </div>
        <div className="payment-method-content">
          <PaymentForm 
            onSuccess={handlePaymentMethodSuccess}
            onError={handlePaymentMethodError}
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SellerSpinner />;
  }

  return (
    <div className="seller-billing-page">
      <div className="page-header">
        <h1>Pagamenti e Fatturazione</h1>
        <p>Gestisci le informazioni di fatturazione e i metodi di pagamento</p>
      </div>

      {message && (
        <div className={`message ${message.includes('successo') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Sezione Informazioni di Fatturazione */}
      <div className="billing-section">
        {showBillingForm ? (
          <>
            <h2>Informazioni di Fatturazione</h2>
            <form onSubmit={handleSaveBillingInfo} className="billing-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="business_name">Nome Azienda *</label>
                  <input
                    type="text"
                    id="business_name"
                    name="business_name"
                    value={billingInfo?.business_name || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Nome della tua azienda"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone_number">Telefono *</label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={billingInfo?.phone_number || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="+39 123 456 7890"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="vat_number">Partita IVA *</label>
                  <input
                    type="text"
                    id="vat_number"
                    name="vat_number"
                    value={billingInfo?.vat_number || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="IT12345678901"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="fiscal_code">Codice Fiscale *</label>
                  <input
                    type="text"
                    id="fiscal_code"
                    name="fiscal_code"
                    value={billingInfo?.fiscal_code || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="RSSMRA80A01H501U"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="univocal_code">Codice Univoco</label>
                  <input
                    type="text"
                    id="univocal_code"
                    name="univocal_code"
                    value={billingInfo?.univocal_code || ''}
                    onChange={handleInputChange}
                    placeholder="Codice univoco"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="pec">PEC</label>
                  <input
                    type="email"
                    id="pec"
                    name="pec"
                    value={billingInfo?.pec || ''}
                    onChange={handleInputChange}
                    placeholder="azienda@pec.it"
                  />
                </div>
              </div>



              <div className="form-group">
                <label htmlFor="billing_address">Indirizzo di Fatturazione *</label>
                <input
                  type="text"
                  id="billing_address"
                  name="billing_address"
                  value={billingInfo?.billing_address || ''}
                  onChange={handleInputChange}
                  required
                  placeholder="Via Roma 123"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billing_city">Città *</label>
                  <input
                    type="text"
                    id="billing_city"
                    name="billing_city"
                    value={billingInfo?.billing_city || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Milano"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="billing_zip">CAP *</label>
                  <input
                    type="text"
                    id="billing_zip"
                    name="billing_zip"
                    value={billingInfo?.billing_zip || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="20100"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="billing_province">Provincia *</label>
                  <input
                    type="text"
                    id="billing_province"
                    name="billing_province"
                    value={billingInfo?.billing_province || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="MI"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="billing_country">Paese *</label>
                  <input
                    type="text"
                    id="billing_country"
                    name="billing_country"
                    value={billingInfo?.billing_country || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Italia"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Salva Informazioni
                </button>
              </div>
            </form>
          </>
        ) : (
          <BillingSummary />
        )}
      </div>

      {/* Sezione Documenti */}
      <div className="documents-section">
        <h2>Documenti</h2>
        <div className="documents-grid">
          <div className="document-card">
            <h3>Visura Camerale</h3>
            <p>Carica la visura camerale della tua azienda</p>
            {billingInfo?.visura_doc ? (
              <div className="document-uploaded">
                <span>✓ Documento caricato</span>
                <div className="document-actions">
                  <a href={billingInfo.visura_doc} target="_blank" rel="noopener">
                    Visualizza
                  </a>
                  <button 
                    type="button" 
                    className="btn-remove"
                    onClick={() => handleDocumentRemove('visura')}
                    disabled={uploadingDoc}
                  >
                    {uploadingDoc ? 'Rimozione...' : 'Rimuovi'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="document-upload">
                <input
                  type="file"
                  id="visura_doc"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleDocumentUpload(e, 'visura')}
                  disabled={uploadingDoc}
                />
                <label htmlFor="visura_doc" className="upload-btn">
                  {uploadingDoc ? 'Caricamento...' : 'Carica Visura'}
                </label>
              </div>
            )}
          </div>

          <div className="document-card">
            <h3>Documento di Identità</h3>
            <p>Carica un documento di identità valido</p>
            {billingInfo?.identity_doc ? (
              <div className="document-uploaded">
                <span>✓ Documento caricato</span>
                <div className="document-actions">
                  <a href={billingInfo.identity_doc} target="_blank" rel="noopener">
                    Visualizza
                  </a>
                  <button 
                    type="button" 
                    className="btn-remove"
                    onClick={() => handleDocumentRemove('identity')}
                    disabled={uploadingDoc}
                  >
                    {uploadingDoc ? 'Rimozione...' : 'Rimuovi'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="document-upload">
                <input
                  type="file"
                  id="identity_doc"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleDocumentUpload(e, 'identity')}
                  disabled={uploadingDoc}
                />
                <label htmlFor="identity_doc" className="upload-btn">
                  {uploadingDoc ? 'Caricamento...' : 'Carica Documento'}
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sezione Metodo di Pagamento */}
      <div className="payment-section">
        <h2>Metodi di Pagamento</h2>
        
        {loadingPaymentMethods ? (
          <div className="loading-payment-methods">
            <p>Caricamento metodi di pagamento...</p>
          </div>
        ) : (paymentMethods || []).length > 0 ? (
          <div className="payment-methods-list">
                          {(paymentMethods || []).map((method) => (
              <div key={method.payment_method_id} className="payment-method-item">
                <div className="payment-method-info">
                  <div className="payment-method-icon">
                    {method.type === 'card' ? '💳' : '🏦'}
                  </div>
                  <div className="payment-method-details">
                    <h4>
                      {method.type === 'card' 
                        ? `${method.brand?.toUpperCase() || 'Carta'} •••• ${method.last4}`
                        : `${method.bank_name || 'Conto Bancario'} •••• ${method.last4}`
                      }
                      {method.is_default ? <span className="default-badge">Default</span> : null}
                    </h4>
                    <p>
                      {method.type === 'card' 
                        ? `Scade ${method.card_exp_month}/${method.card_exp_year}`
                        : 'Conto bancario'
                      }
                    </p>
                  </div>
                </div>
                <div className="payment-method-actions">
                  {!method.is_default && (
                    <button 
                      className="btn-secondary"
                      onClick={() => handleSetDefaultPaymentMethod(method.payment_method_id)}
                    >
                      Imposta Default
                    </button>
                  )}
                  <button 
                    className="btn-remove"
                    onClick={() => handleRemovePaymentMethod(method.payment_method_id)}
                  >
                    Rimuovi
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="payment-info">
            <p>Nessun metodo di pagamento configurato. Aggiungi un metodo per gestire le fatture e i pagamenti automatici.</p>
          </div>
        )}
        
        <div className="payment-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowPaymentModal(true)}
          >
            {(paymentMethods || []).length > 0 ? 'Aggiungi Altro Metodo' : 'Configura Metodo di Pagamento'}
          </button>
        </div>
      </div>

      {/* Sezione Fatture */}
      <div className="invoices-section">
        <h2>Fatture</h2>
        {(invoices || []).length > 0 ? (
          <>
            <div className="invoices-list">
              {(invoices || []).map((invoice) => (
                <div key={invoice.id} className="invoice-item">
                  <div className="invoice-info">
                    <h4>Fattura #{invoice.invoice_number}</h4>
                    <p className="invoice-date">
                      {new Date(invoice.created_at).toLocaleDateString('it-IT')}
                    </p>
                    {invoice.charge_description && (
                      <p className="invoice-description">{invoice.charge_description}</p>
                    )}
                    {invoice.stripe_charge_id && (
                      <p className="stripe-charge-id">
                        <strong>Id transazione:</strong> {invoice.stripe_charge_id}
                      </p>
                    )}
                  </div>
                  <div className="invoice-actions">
                    <div className="invoice-amount">
                      <span className="amount">€{Number(invoice.total_amount || invoice.amount).toFixed(2)}</span>
                      <span className={`invoice-status ${invoice.status}`}>
                        {invoice.status === 'paid' ? 'Pagata' : 
                         invoice.status === 'open' ? 'Aperta' : 
                         invoice.status === 'draft' ? 'Bozza' : invoice.status}
                      </span>
                    </div>
                    {invoice.pdf_path && (
                      <button 
                        className="btn-view-pdf"
                        onClick={() => window.open(`/media/${invoice.pdf_path}`, '_blank')}
                        title="Visualizza PDF"
                      >
                        📄 Visualizza PDF
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Pagination 
              pagination={invoicesPagination} 
              onPageChange={(page) => loadInvoices(page)}
              type="invoices"
            />
          </>
        ) : (
          <div className="invoices-placeholder">
            <p>Nessuna fattura emessa</p>
            <p>Le fatture appariranno qui una volta emesse.</p>
          </div>
        )}
      </div>

      {/* Sezione Transazioni */}
      <div className="transactions-section">
        <h2>Transazioni</h2>
        {(charges || []).length > 0 ? (
          <>
            <div className="transactions-list">
              {(charges || []).map((charge) => (
                <div key={charge.id} className="transaction-item">
                  <div className="transaction-info">
                    <h4>{charge.description || charge.charge_type}</h4>
                    <p className="transaction-date">
                      {new Date(charge.created_at).toLocaleDateString('it-IT')}
                    </p>
                    {charge.payment_method_type && (
                      <p className="payment-method">
                        {charge.payment_method_type} {charge.last4 ? `••••${charge.last4}` : ''}
                      </p>
                    )}
                  </div>
                  <div className={`transaction-amount ${charge.status}`}>
                    <span className="amount">€{Number(charge.amount).toFixed(2)}</span>
                    <span className={`transaction-status ${charge.status}`}>
                      {charge.status === 'succeeded' ? 'Completata' : 
                       charge.status === 'failed' ? 'Fallita' : 
                       charge.status === 'pending' ? 'In elaborazione' : charge.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <Pagination 
              pagination={chargesPagination} 
              onPageChange={(page) => loadCharges(page)}
              type="charges"
            />
          </>
        ) : (
          <div className="transactions-placeholder">
            <p>Nessuna transazione</p>
            <p>Le transazioni appariranno qui una volta effettuate.</p>
          </div>
        )}
      </div>

      {/* Modali */}
      {showPaymentModal && <PaymentMethodModal />}
    </div>
  );
}

export default SellerBillingPage; 